import type { InternalUser } from '@ricoh/ria.client.core/model';

import { ClientError } from '@ricoh/ria.client.core/errors';
import { getData, getHeaders, ServiceServerError } from '@ricoh/ria.client.core/plugins/swr/common';
import { createFetcher } from '@ricoh/ria.client.core/plugins/swr/fetcher';
import { Url } from '@ricoh/ria.client.core/utils/es/url';

import axiosPlugin from '~/plugins/axios';

import type { GetUserResponse, SigninResponse, VerifiedResult, VerifyResponse } from './_fetcher.type';
import type {
  ChangePasswordRequestData,
  CookieSettings,
  CreatePasswordRequestData,
  ExternalSigninRequestData,
  RequestResetPasswordRequestData,
  RequestSendOTPRequestData,
  SigninRequestData,
  VerifyOTPRequestData,
  VerifyRequestData,
} from './_request.type';

import requestConfig from './_request';

const { options, endpoints, createError } = requestConfig;

const client = axiosPlugin.createClient(options);

/**
 * Get user.
 *
 * @returns {InternalUser} User .
 */
async function getUser(): Promise<InternalUser> {
  try {
    const accountInfo = getData<GetUserResponse>(await client.request(endpoints.getAccountInfo()));

    return {
      $is: 'user',
      ...accountInfo,
      roles: {},
    };
  } catch (exception) {
    const error = createError('getAccountInfo', 'Failed to get account info.', exception);
    if (error instanceof ServiceServerError && error.status === 401) {
      return {
        $is: 'guest',
      };
    }

    throw error;
  }
}

/** Signin fetcher. */
export const signinFetcher = createFetcher<SigninRequestData, InternalUser>(endpoints.signin.key, async (requestData) => {
  try {
    const response = await client.request(endpoints.signin(requestData));
    if (response.status === 204) {
      return await getUser();
    }

    const { requiresTwoFactor } = getData<SigninResponse>(response);
    if (requiresTwoFactor) {
      return {
        $is: 'unverifiedUser',
        email: requestData.email,
      };
    }

    return {
      $is: 'guest',
    };
  } catch (exception) {
    throw createError('signin', 'Failed to sign in.', exception);
  }
});

/** Signout fetcher. */
export const signoutFetcher = createFetcher(endpoints.signout.key, async () => {
  try {
    await client.request(endpoints.signout());
  } catch (exception) {
    throw createError('signout', 'Failed to sign out.', exception);
  }
});

/** VerifyOTP fetcher. */
export const verifyOTPFetcher = createFetcher<VerifyOTPRequestData, InternalUser>(endpoints.verifyOTP.key, async (requestData) => {
  try {
    await client.request(endpoints.verifyOTP(requestData));

    // * After verify OTP, the user will be signed in.
    return await getUser();
  } catch (exception) {
    throw createError('verifyOTP', 'Failed to verify OTP.', exception);
  }
});

/** Request Send OTP fetcher. */
export const requestSendOTPFetcher = createFetcher<RequestSendOTPRequestData>(endpoints.requestSendOTP.key, async (requestData) => {
  try {
    await client.request(endpoints.requestSendOTP(requestData));
  } catch (exception) {
    throw createError('requestSendOTP', 'Failed to request send OTP.', exception);
  }
});

/** GetUser fetcher. */
export const getUserFetcher = createFetcher<void, InternalUser>(endpoints.getAccountInfo.key, getUser);

/** ChangePassword fetcher. */
export const changePasswordFetcher = createFetcher<ChangePasswordRequestData>(endpoints.changePassword.key, async (requestData) => {
  try {
    await client.request(endpoints.changePassword(requestData));
  } catch (exception) {
    throw createError('changePassword', 'Failed to change password.', exception);
  }
});

/** VerifyCreatePassword fetcher. */
export const verifyInitPasswordFetcher = createFetcher<VerifyRequestData, VerifiedResult>(endpoints.verifyInitPassword.key, async (requestData) => {
  try {
    const { token } = getData<VerifyResponse>(await client.request(endpoints.verifyInitPassword(requestData)));

    return {
      key: requestData.key,
      token,
    };
  } catch (exception) {
    throw createError('verifyInitPassword', 'Failed to verify email.', exception);
  }
});

/** InitPassword fetcher. */
export const initPasswordFetcher = createFetcher<CreatePasswordRequestData, InternalUser>(endpoints.initPassword.key, async (requestData) => {
  try {
    await client.request(endpoints.initPassword(requestData));

    // * After reset password, the user will be signed in.
    return await getUser();
  } catch (exception) {
    throw createError('initPassword', 'Failed to create password.', exception);
  }
});

/** RequestResetPassword fetcher. */
export const requestResetPasswordFetcher = createFetcher<RequestResetPasswordRequestData>(endpoints.requestResetPassword.key, async (requestData) => {
  try {
    await client.request(endpoints.requestResetPassword(requestData));
  } catch (exception) {
    throw createError('requestResetPassword', 'Failed to request reset password.', exception);
  }
});

/** VerifyResetPassword fetcher. */
export const verifyResetPasswordFetcher = createFetcher<VerifyRequestData, VerifiedResult>(endpoints.verifyResetPassword.key, async (requestData) => {
  try {
    const { token } = getData<VerifyResponse>(await client.request(endpoints.verifyResetPassword(requestData)));

    return {
      key: requestData.key,
      token,
    };
  } catch (exception) {
    throw createError('verifyResetPassword', 'Failed to verify reset password.', exception);
  }
});

/** ResetPassword fetcher. */
export const resetPasswordFetcher = createFetcher<CreatePasswordRequestData, InternalUser>(endpoints.resetPassword.key, async (requestData) => {
  try {
    await client.request(endpoints.resetPassword(requestData));

    // * After reset password, the user will be signed in.
    return await getUser();
  } catch (exception) {
    throw createError('resetPassword', 'Failed to reset password.', exception);
  }
});

/** ExternalSignin fetcher. */
export const externalSigninFetcher = createFetcher<ExternalSigninRequestData, Url>(endpoints.externalSignin.key, async (requestData) => {
  try {
    const response = await client.request(endpoints.externalSignin(requestData));
    const { location } = getHeaders(response, 'Location');
    if (!location) {
      throw new ClientError({
        module: 'service',
        message: 'Not found location of external signin',
        cause: response,
      });
    }

    return new Url(location);
  } catch (exception) {
    throw createError('externalSignin', 'Failed to external signin.', exception);
  }
});

/** Get CookieSetting fetcher. */
export const getCookieSettingsFetcher = createFetcher<void, CookieSettings | undefined>(endpoints.getCookieSettings.key, async () => {
  try {
    const response = await client.request(endpoints.getCookieSettings());

    if (response.status === 204) {
      return undefined;
    }

    return getData<CookieSettings>(response);
  } catch (exception) {
    throw createError('getCookieSettings', 'Failed to get cookie settings.', exception);
  }
});

/** Set CookieSetting fetcher. */
export const setCookieSettingsFetcher = createFetcher<CookieSettings, CookieSettings>(endpoints.setCookieSettings.key, async (requestData) => {
  try {
    return await client.request(endpoints.setCookieSettings(requestData));
  } catch (exception) {
    throw createError('setCookieSettings', 'Failed to set cookie settings.', exception);
  }
});
